import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface BespokeSectionErrorBoundaryProps {
  /** Rendered in place of the bespoke section if it throws (the standard grid). */
  fallback: ReactNode;
  children: ReactNode;
}

interface BespokeSectionErrorBoundaryState {
  hasError: boolean;
}

/**
 * Renders `fallback` (the standard grid layout) if a bespoke `layout_json`
 * section throws during render.
 *
 * `resolveBespokeSection` already pre-filters layouts that fail the compile
 * gate (`getLayoutCompileError`), but the gate walks a different code path than
 * the renderer, so a malformed-but-known-type node can still throw at render
 * time. Without this boundary that throw bubbles to the app's top-level
 * `SentryErrorBoundary` and blanks the whole page; here we degrade to the
 * standard grid for just this section instead.
 */
export class BespokeSectionErrorBoundary extends Component<
  BespokeSectionErrorBoundaryProps,
  BespokeSectionErrorBoundaryState
> {
  state: BespokeSectionErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): BespokeSectionErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
